// src/components/admin/template/TemplateGroupingsEditor.tsx

import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, IconButton, Checkbox } from '@mui/material';
import RGL, { WidthProvider } from 'react-grid-layout';
import {
  TemplateWithIds,
  GroupingWithId,
  PropertyTemplatesWithIds,
} from './MetadataInterfaces';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import MoveIcon from '@mui/icons-material/OpenWith';
import { useSidePanel } from '../../../contexts/SidePanelContext';
import GroupingForm from './GroupingForm';
import MoveGroupingForm from './MoveGroupingForm';
import { SelectionType } from './SelectionModeToggle';

const ReactGridLayout = WidthProvider(RGL);

interface TemplateGroupingsEditorProps {
  template: TemplateWithIds;
  onUpdateGroupings: (updatedGroupings: GroupingWithId[]) => void;
  propertyTemplates: PropertyTemplatesWithIds;
  currentPropertyType: string;
  isSelectionMode: boolean;
  selectedItems: string[];
  onSelectionChange: (type: SelectionType, id: string) => void;
}

const TemplateGroupingsEditor: React.FC<TemplateGroupingsEditorProps> = ({
  template,
  onUpdateGroupings,
  propertyTemplates,
  currentPropertyType,
  isSelectionMode,
  selectedItems,
  onSelectionChange,
}) => {
  const [groupings, setGroupings] = useState<GroupingWithId[]>(
    template.groupings || [],
  );
  const { showPanel, hidePanel } = useSidePanel();

  useEffect(() => {
    if (template && template.groupings) {
      setGroupings(template.groupings);
    }
  }, [template]);

  const onLayoutChange = (layout: RGL.Layout[]) => {
    const newGroupings = layout
      .sort((a, b) => a.y - b.y)
      .map((item) => groupings.find((g) => g.id === item.i))
      .filter((grouping): grouping is GroupingWithId => grouping !== undefined);

    setGroupings(newGroupings);
    onUpdateGroupings(newGroupings);
  };

  const handleAddGrouping = () => {
    showPanel(
      'Add Grouping',
      <GroupingForm
        onSave={(newGrouping) => {
          const groupingWithId = { ...newGrouping, id: crypto.randomUUID() };
          setGroupings([...groupings, groupingWithId]);
          onUpdateGroupings([...groupings, groupingWithId]);
          hidePanel();
        }}
        onCancel={hidePanel}
      />,
    );
  };

  const handleEditGrouping = (grouping: GroupingWithId) => {
    showPanel(
      'Edit Grouping',
      <GroupingForm
        grouping={grouping}
        onSave={(updatedGrouping) => {
          const updatedGroupings = groupings.map((g) =>
            g.id === grouping.id ? { ...updatedGrouping, id: g.id } : g,
          );
          setGroupings(updatedGroupings);
          onUpdateGroupings(updatedGroupings);
          hidePanel();
        }}
        onCancel={hidePanel}
      />,
    );
  };

  const handleDeleteGrouping = (groupingId: string) => {
    const updatedGroupings = groupings.filter((g) => g.id !== groupingId);
    setGroupings(updatedGroupings);
    onUpdateGroupings(updatedGroupings);
  };

  const handleMoveGrouping = (grouping: GroupingWithId) => {
    showPanel(
      'Move/Copy Grouping',
      <MoveGroupingForm
        grouping={grouping}
        currentPropertyType={currentPropertyType}
        propertyTemplates={propertyTemplates}
        onSave={(grouping, destPropertyType, isCopy) => {
          const updatedTemplates = { ...propertyTemplates };

          if (!isCopy) {
            // Remove grouping from current property type if it's a move operation
            updatedTemplates[currentPropertyType].groupings = updatedTemplates[
              currentPropertyType
            ].groupings.filter((g: GroupingWithId) => g.id !== grouping.id);
          }

          // Add grouping to destination property type
          const groupingToAdd = isCopy
            ? { ...grouping, id: crypto.randomUUID() }
            : grouping;
          updatedTemplates[destPropertyType].groupings = [
            ...updatedTemplates[destPropertyType].groupings,
            groupingToAdd,
          ];

          // Update the current view if we're still in the same property type
          if (currentPropertyType === destPropertyType || !isCopy) {
            const updatedGroupings =
              updatedTemplates[currentPropertyType].groupings;
            setGroupings(updatedGroupings);
            onUpdateGroupings(updatedGroupings);
          }

          hidePanel();
        }}
        onCancel={hidePanel}
      />,
    );
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Typography variant="h6" gutterBottom>
          Groupings
        </Typography>
        <Button
          startIcon={<AddIcon />}
          onClick={handleAddGrouping}
          variant="contained"
          color="primary"
        >
          Add Grouping
        </Button>
      </Box>
      <ReactGridLayout
        className="layout"
        layout={groupings.map((grouping, index) => ({
          i: grouping.id,
          x: 0,
          y: index,
          w: 1,
          h: 1,
        }))}
        cols={1}
        rowHeight={40}
        width={250}
        onLayoutChange={onLayoutChange}
        isResizable={false}
        draggableHandle=".drag-handle"
      >
        {groupings.map((grouping) => (
          <div key={grouping.id}>
            <Box
              sx={{
                p: 1,
                height: '100%',
                bgcolor: 'background.paper',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  minWidth: 0, // This allows the text to shrink
                  flex: 1,
                }}
              >
                {isSelectionMode && (
                  <Checkbox
                    checked={selectedItems.includes(grouping.id)}
                    onChange={() => onSelectionChange('groupings', grouping.id)}
                    onClick={(e) => e.stopPropagation()}
                  />
                )}
                <Typography
                  sx={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {grouping.Title || `KEY=${grouping.Key}`}
                </Typography>
              </Box>
              <Box>
                <IconButton
                  onClick={() => handleMoveGrouping(grouping)}
                  size="small"
                >
                  <MoveIcon fontSize="small" />
                </IconButton>
                <IconButton
                  onClick={() => handleEditGrouping(grouping)}
                  size="small"
                >
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton
                  onClick={() => handleDeleteGrouping(grouping.id)}
                  size="small"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
                <IconButton className="drag-handle" size="small">
                  ⋮
                </IconButton>
              </Box>
            </Box>
          </div>
        ))}
      </ReactGridLayout>
    </Box>
  );
};

export default TemplateGroupingsEditor;
